import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Checkbox,
  FormLabel,
  Radio,
  RadioGroup,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Stack,
} from '@chakra-ui/react';

import { SALES_FORMAT_OPTIONS } from 'src/modules/CatalogV2/const';
import { IntegerNumberInput, Input } from 'src/chakra-components';
import { CurrencyInput } from 'src/chakra-components/Input/CurrencyInput';

import { getFormFieldSchema } from './formFieldSchema';
import { ReactQuillEditor } from './ReactQuillEditor';
import { SupportInformation } from './SupportInformation';
import messages from './messages';

const computeDiscountPrice = (price, discount) => {
  const discountFraction = discount / 100;

  return price * (1 - discountFraction);
};

export const Form = ({ product, onSubmit }) => {
  const intl = useIntl();

  const {
    description,
    salesFormat,
    increaseAmount,
    minimumPurchaseAmount,
    fullPrice,
    discountPercentage,
    suggestedRetailPrice,
    isPublished,
    isInStock,
  } = product;

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      description,
      salesFormat,
      minimumPurchaseAmount,
      increaseAmount,
      fullPrice,
      discountPercentage,
      suggestedRetailPrice,
      isPublished,
      isInStock,
    },
    resolver: yupResolver(getFormFieldSchema(intl)),
  });

  const [
    watchSalesFormat,
    watchIncreaseAmount,
    watchFullPrice,
    watchDiscountPercentage,
  ] = watch([
    'salesFormat',
    'increaseAmount',
    'fullPrice',
    'discountPercentage',
  ]);

  const isBoxSaleFormat = watchSalesFormat === SALES_FORMAT_OPTIONS.BOX;
  const shouldShowDiscountInfo =
    watchFullPrice > 0 &&
    watchDiscountPercentage > 0 &&
    watchDiscountPercentage <= 100;

  const appliedDiscountValue = shouldShowDiscountInfo
    ? watchFullPrice -
      computeDiscountPrice(watchFullPrice, watchDiscountPercentage)
    : 0;

  return (
    <form onSubmit={handleSubmit(onSubmit)} aria-label="product data form">
      <Box my={3} display="flex" flexDirection="column">
        <ReactQuillEditor
          control={control}
          name="description"
          label={messages.descriptionLabel}
          placeholder={messages.descriptionPlaceholder}
        />
        <Box my={1} py={1}>
          <Controller
            name="salesFormat"
            control={control}
            render={({ field }) => (
              <>
                <FormLabel fontSize="lg" fontWeight="bold" htmlFor={field.name}>
                  <FormattedMessage {...messages.salesFormatLabel} />
                </FormLabel>
                <RadioGroup {...field}>
                  <Stack>
                    {Object.values(SALES_FORMAT_OPTIONS).map(option => (
                      <Radio value={option} key={option}>
                        <FormattedMessage
                          {...messages.salesFormatRadioLabelByOption}
                          values={{ option }}
                        />
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              </>
            )}
          />
        </Box>
        <Box my={1} py={1}>
          <Controller
            name="minimumPurchaseAmount"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <IntegerNumberInput
                {...field}
                min={1}
                step={1}
                error={error}
                label={messages.minimumPurchaseAmountLabel}
                helperText={{
                  ...messages.minimumPurchaseAmountHelperText,
                  values: { salesFormat: watchSalesFormat },
                }}
              />
            )}
          />
        </Box>
        {isBoxSaleFormat && (
          <Box my={1} py={1}>
            <Controller
              name="increaseAmount"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <IntegerNumberInput
                  {...field}
                  min={1}
                  step={1}
                  error={error}
                  label={messages.increaseAmountLabel}
                />
              )}
            />
          </Box>
        )}
        <Box my={1} py={1}>
          <Controller
            name="fullPrice"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <CurrencyInput
                {...field}
                error={error}
                label={messages.fullPriceLabel}
              />
            )}
          />
        </Box>
        <Box my={1} py={1}>
          <Controller
            name="discountPercentage"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                onBlur={event => {
                  const numberValue = Number(event.target.value);

                  if (numberValue <= 0) field.onChange(0);
                  else if (numberValue > 100) field.onChange(100);

                  field.onBlur(event);
                }}
                error={error}
                label={messages.discountPercentageLabel}
                helperText={messages.discountPercentageHelperText}
                leftElement="%"
                type="number"
              />
            )}
          />
        </Box>
        <Box my={1} py={1}>
          <Controller
            name="suggestedRetailPrice"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <CurrencyInput
                {...field}
                error={error}
                label={messages.suggestedRetailPriceLabel}
                helperText={{
                  ...messages.suggestedRetailPriceHelperText,
                  values: { salesFormat: watchSalesFormat },
                }}
              />
            )}
          />
        </Box>
        <Box my={4} py={1} display="flex" flexDirection="column" gap={4}>
          <Controller
            name="isPublished"
            control={control}
            render={({ field }) => (
              <Checkbox
                size="lg"
                id={field.name}
                isChecked={field.value}
                {...field}
              >
                <FormattedMessage {...messages.publishedToggleLabel} />
              </Checkbox>
            )}
          />
          <Controller
            name="isInStock"
            control={control}
            render={({ field }) => (
              <Checkbox
                size="lg"
                id={field.name}
                isChecked={field.value}
                {...field}
              >
                <FormattedMessage {...messages.inStockToggleLabel} />
              </Checkbox>
            )}
          />
        </Box>
        <StatGroup
          p={4}
          my={2}
          shadow="md"
          borderWidth="1px"
          display="flex"
          flexDirection={{ base: 'column', md: 'row' }}
        >
          {isBoxSaleFormat && (
            <Stat m={2}>
              <StatLabel>
                <FormattedMessage {...messages.boxPriceLabel} />
              </StatLabel>
              <StatNumber>
                <FormattedNumber
                  value={watchIncreaseAmount * watchFullPrice}
                  format="currency"
                />
              </StatNumber>
            </Stat>
          )}
          {shouldShowDiscountInfo && (
            <Stat m={2}>
              <StatLabel>
                <FormattedMessage {...messages.priceWithDiscountTip} />
              </StatLabel>
              <StatNumber>
                <FormattedNumber
                  value={computeDiscountPrice(
                    watchFullPrice,
                    watchDiscountPercentage,
                  )}
                  format="currency"
                />
              </StatNumber>
            </Stat>
          )}
          <Stat m={2}>
            <StatLabel>
              <FormattedMessage {...messages.appliedDiscountTip} />
            </StatLabel>
            <StatNumber>
              <FormattedNumber value={appliedDiscountValue} format="currency" />
            </StatNumber>
          </Stat>
        </StatGroup>
        <SupportInformation />
        <Button
          colorScheme="blackAlpha"
          size="lg"
          mt={4}
          type="submit"
          alignSelf={{ base: 'center', md: 'start' }}
        >
          <FormattedMessage {...messages.buttonLabel} />
        </Button>
      </Box>
    </form>
  );
};
